import React from 'react';
import {
  Box,
  Container,
  Typography,
  Stack,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import VerifiedIcon from '@mui/icons-material/Verified';
import { useLanguage } from '../contexts/LanguageContext';
import { Reveal, SpotCard, Glow, GridTexture, BRAND, BRAND_GRAD } from './premium';

const AboutSection: React.FC = () => {
  const { t } = useLanguage();

  const teamStats = [
    { label: t.about.team.experience, icon: <AccessTimeIcon /> },
    { label: t.about.team.projects, icon: <EmojiEventsIcon /> },
    { label: t.about.team.certified, icon: <VerifiedIcon /> },
  ];

  return (
    <Box id="about" sx={{ position: 'relative', overflow: 'hidden', py: { xs: 9, md: 13 }, backgroundColor: '#FFFFFF' }}>
      <Glow color="#6DABE4" bottom="-12%" right="-8%" size={460} opacity={0.13} />
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Reveal>
          <Grid container spacing={{ xs: 4, md: 8 }} alignItems="center">
            <Grid size={{ xs: 12, lg: 6 }}>
              <Box sx={{ textAlign: { xs: 'center', lg: 'left' } }}>
                <Box sx={{ width: 40, height: 3, borderRadius: 3, background: BRAND_GRAD, mx: { xs: 'auto', lg: 0 }, mb: 2.5 }} />
                <Typography component="h2" sx={{ mb: 3, fontSize: { xs: '2rem', md: '2.6rem' }, fontWeight: 300, color: '#1A2332', letterSpacing: '-0.01em', textWrap: 'balance' }}>
                  {t.about.title}
                </Typography>
                <Typography sx={{ mb: 4, fontSize: { xs: '1rem', md: '1.125rem' }, color: '#4A5568', lineHeight: 1.75, textWrap: 'pretty' }}>
                  {t.about.description}
                </Typography>
                <Typography variant="h5" sx={{ mb: 3, fontSize: { xs: '1.25rem', md: '1.5rem' }, fontWeight: 600, color: '#1A2332' }}>
                  {t.about.team.title}
                </Typography>
                <Grid container spacing={2}>
                  {t.about.team.members.map((member, index) => (
                    <Grid size={{ xs: 12, sm: 6 }} key={index}>
                      <SpotCard sx={{ p: 2.5, height: '100%', borderRadius: '14px', border: '1px solid #E6EEF9', boxShadow: '0 6px 18px -12px rgba(0,85,184,0.16)' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: BRAND, mb: 0.8, fontSize: '0.9rem' }}>
                          {member.role}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#4A5568', fontSize: '0.82rem', lineHeight: 1.5 }}>
                          {member.skills}
                        </Typography>
                      </SpotCard>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Grid>

            <Grid size={{ xs: 12, lg: 6 }}>
              <Box sx={{ position: 'relative' }}>
                <GridTexture opacity={0.05} />
                <Stack spacing={2.5} sx={{ position: 'relative', zIndex: 1 }}>
                  {teamStats.map((stat, index) => (
                    <SpotCard key={index} sx={{ p: { xs: 2.5, md: 3 }, borderRadius: '16px', borderLeft: `3px solid ${BRAND}`, background: 'linear-gradient(135deg,#F1F5FF 0%,#FAFBFF 100%)', boxShadow: '0 10px 30px -16px rgba(0,85,184,0.14)' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 46, height: 46, borderRadius: '12px', background: 'linear-gradient(135deg,rgba(0,85,184,0.12),rgba(109,171,228,0.14))', color: BRAND, flexShrink: 0, '& svg': { fontSize: '1.4rem' } }}>
                          {stat.icon}
                        </Box>
                        <Typography sx={{ fontWeight: 500, color: '#1A2332', fontSize: { xs: '0.9rem', md: '1rem' } }}>
                          {stat.label}
                        </Typography>
                      </Box>
                    </SpotCard>
                  ))}
                </Stack>
              </Box>
            </Grid>
          </Grid>
        </Reveal>
      </Container>
    </Box>
  );
};

export default AboutSection;
